// Send, Copy link and PDF on the office's invoice (QA-341). The link is the server's (invoices.send
// and invoices.copyLink build it with the one customer-link builder the portal uses); the office
// never puts a URL together itself. Blueprint draws them with ui-web's parts (BL-189): Send a Button,
// Copy link and PDF Links (DESIGN.md §3: two buttons a part), what was done a Notice, the link a read-only
// TextField; Riverstone keeps its own.

import { useEffect, useRef, useState } from 'react';
import { RpcError } from '@flowology/client';
import { Button, Link, Notice, TextField } from '@flowology/ui-web/fl';
import { useDesign } from '../blueprint/design.ts';
import { client } from '../api.ts';
import { useMirror } from '../shared/mirror.ts';
import { invoiceShare, sentText, type InvoiceSummary } from './review-model.ts';
import './invoice-actions.css';

const message = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'Something went wrong.';

export function InvoiceActions({
  invoice,
  onSent,
  sendWord = 'Send to customer',
  sendVariant = 'primary',
}: {
  invoice: Pick<InvoiceSummary, 'id' | 'number' | 'status'>;
  onSent: () => void;
  /** "Send again" where it was sent before (the invoice sheet). */
  sendWord?: string;
  /** Blueprint: secondary where another action is the part's one primary (Record a payment). */
  sendVariant?: 'primary' | 'secondary';
}) {
  const bp = useDesign() === 'blueprint';
  const can = invoiceShare(invoice.status);
  const mirroring = useMirror();
  const [busy, setBusy] = useState<'send' | 'link' | 'pdf' | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState<string | null>(null);
  // the link's box selects all of it as it takes the focus, to copy by hand (Blueprint's TextField)
  const linkBox = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    const box = linkBox.current;
    if (!box) return;
    const all = () => box.select();
    box.addEventListener('focus', all);
    return () => box.removeEventListener('focus', all);
  }, [link, bp]);

  const run = async (what: 'send' | 'link' | 'pdf', fn: () => Promise<void>) => {
    setBusy(what);
    setError(null);
    setNote(null);
    try {
      await fn();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(null);
    }
  };

  const send = () =>
    run('send', async () => {
      const r = await client.call<{ sent: { channel: 'sms' | 'email' }[] }>('invoices.send', {
        invoiceId: invoice.id,
      });
      setNote(sentText(invoice.number, r.sent[0]?.channel ?? 'sms'));
      onSent();
    });

  const copy = () =>
    run('link', async () => {
      const r = await client.call<{ url: string; expiry: string }>('invoices.copyLink', {
        invoiceId: invoice.id,
      });
      setLink(r.url);
      // Where the browser won't write to the clipboard, the link stays on screen to copy by hand.
      const copied = await navigator.clipboard?.writeText(r.url).then(
        () => true,
        () => false,
      );
      setNote(copied ? `Link copied. It ${r.expiry}.` : `Copy the link below. It ${r.expiry}.`);
    });

  const pdf = () =>
    run('pdf', async () => {
      const r = await client.call<{ filename: string; base64: string }>('documents.invoicePdf', {
        invoiceId: invoice.id,
      });
      const a = document.createElement('a');
      a.href = `data:application/pdf;base64,${r.base64}`;
      a.download = r.filename;
      a.click();
    });

  if (bp)
    return (
      <div className="invoice-actions">
        <div className="row">
          {can.share && (
            <>
              {/* while the company mirrors the import source, its invoices go to the customer from there */}
              {!mirroring && (
                // on a phone Send takes its own row and the two Links share the next
                <span className="invoice-send">
                  <Button
                    variant={sendVariant}
                    size="touch-on-phone"
                    isBusy={busy === 'send'}
                    isDisabled={!!busy && busy !== 'send'}
                    onPress={() => void send()}
                  >
                    {busy === 'send' ? 'Sending…' : sendWord}
                  </Button>
                </span>
              )}
              <Link size="touch-on-phone" isDisabled={!!busy} onPress={() => void copy()}>
                {busy === 'link' ? 'Making the link…' : 'Copy link'}
              </Link>
            </>
          )}
          <Link size="touch-on-phone" isDisabled={!!busy} onPress={() => void pdf()}>
            {busy === 'pdf' ? 'Making the PDF…' : 'PDF'}
          </Link>
        </div>
        {can.why && <p className="muted small">{can.why}</p>}
        {note && <Notice tone="done" title={note} />}
        {link && (
          <TextField
            label="Link to the invoice"
            isReadOnly
            value={link}
            onChange={() => undefined}
            inputRef={linkBox}
          />
        )}
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  return (
    <div className="invoice-actions">
      <div className="row">
        {can.share && (
          <>
            {/* while the company mirrors ServiceTitan, its invoices go to the customer from there;
                the link's page already takes no payment then (QA-606) */}
            {!mirroring && (
              <button className="btn primary" disabled={!!busy} onClick={() => void send()}>
                {busy === 'send' ? 'Sending…' : sendWord}
              </button>
            )}
            <button className="btn" disabled={!!busy} onClick={() => void copy()}>
              {busy === 'link' ? 'Making the link…' : 'Copy link'}
            </button>
          </>
        )}
        <button className="btn" disabled={!!busy} onClick={() => void pdf()}>
          {busy === 'pdf' ? 'Making the PDF…' : 'PDF'}
        </button>
      </div>
      {can.why && <p className="muted small">{can.why}</p>}
      {note && (
        <p className="notice ok" role="status">
          {note}
        </p>
      )}
      {link && (
        <label className="f">
          Link to the invoice
          <input className="field" readOnly value={link} onFocus={(e) => e.target.select()} />
        </label>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
